(() => {
  if (customElements.get('andcharlie-walk-hero')) return;

  class AndCharlieWalkHero extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.lifecycle = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.inView = false;
      this.pageActive = true;
      this.resetToWait = false;
      this.runId = 0;
      this.starting = false;
      this.video.muted = true;
      this.video.loop = true;
      this.video.pause();
      this.dataset.playback = 'waiting';
      const options = { signal: this.lifecycle.signal };
      document.addEventListener('visibilitychange', () => this.update(), options);
      this.motion.addEventListener('change', () => this.update(), options);
      window.addEventListener('pagehide', () => { this.pageActive = false; this.update(); }, options);
      window.addEventListener('pageshow', () => { this.pageActive = true; this.update(); }, options);
      this.addEventListener('pointerdown', () => this.update(), options);
      this.video.addEventListener('error', () => this.standby('media-error'), options);
      this.observer = new IntersectionObserver((entries) => {
        this.inView = entries[0].isIntersecting && entries[0].intersectionRatio > 0;
        this.update();
      }, { threshold: [0, 0.01] });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.standby('disconnected');
      this.observer?.disconnect();
      this.lifecycle?.abort();
      this.lifecycle = null;
    }

    eligible() {
      return this.isConnected && this.inView && this.pageActive && !document.hidden && !this.motion.matches;
    }

    standby(reason) {
      this.runId = (this.runId || 0) + 1;
      this.request?.abort();
      this.starting = false;
      this.video?.pause();
      this.resetToWait = true;
      this.dataset.playback = 'waiting';
      this.dataset.waitReason = reason;
      // The exact seven-second poster stays visible; no seeking/decoding while hidden.
    }

    waitFor(event, ready, signal) {
      if (signal.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'));
      if (this.video.error) return Promise.reject(new Error('Media unavailable'));
      if (ready()) return Promise.resolve();
      return new Promise((resolve, reject) => {
        const cleanup = () => {
          this.video.removeEventListener(event, done);
          this.video.removeEventListener('error', failed);
          signal.removeEventListener('abort', aborted);
        };
        const done = () => { if (ready()) { cleanup(); resolve(); } };
        const failed = () => { cleanup(); reject(new Error('Media unavailable')); };
        const aborted = () => { cleanup(); reject(new DOMException('Aborted', 'AbortError')); };
        this.video.addEventListener(event, done);
        this.video.addEventListener('error', failed);
        signal.addEventListener('abort', aborted, { once: true });
      });
    }

    async update() {
      if (!this.eligible()) {
        this.standby(document.hidden ? 'hidden-tab' : this.motion.matches ? 'reduced-motion' : 'offscreen');
        return;
      }
      if (this.starting || (!this.video.paused && this.dataset.playback === 'playing')) return;
      const id = ++this.runId;
      this.request?.abort();
      this.request = new AbortController();
      const { signal } = this.request;
      this.starting = true;
      try {
        await this.waitFor('loadedmetadata', () => this.video.readyState >= 1, signal);
        if (this.resetToWait) {
          const requested = Number(this.dataset.waitTime || 7);
          const target = Math.max(0, Math.min(requested, this.video.duration - 0.05));
          if (Math.abs(this.video.currentTime - target) > 0.015 || this.video.seeking) {
            const seeked = this.waitFor('seeked', () => !this.video.seeking && Math.abs(this.video.currentTime - target) <= 0.015, signal);
            this.video.currentTime = target;
            await seeked;
          }
        }
        if (id !== this.runId || !this.eligible()) return;
        await this.video.play();
        if (id !== this.runId || !this.eligible()) {
          if (!this.eligible()) this.video.pause();
          return;
        }
        await this.waitFor('loadeddata', () => this.video.readyState >= 2, signal);
        if (id !== this.runId || !this.eligible()) return;
        this.resetToWait = false;
        this.dataset.playback = 'playing';
        delete this.dataset.waitReason;
      } catch (error) {
        if (id === this.runId && error.name !== 'AbortError') this.standby('playback-unavailable');
      } finally {
        if (id === this.runId) this.starting = false;
      }
    }
  }

  customElements.define('andcharlie-walk-hero', AndCharlieWalkHero);
})();
